// src/pages/PracticeSessionsClinic/index.js
//
// Uygulamalar — klinik geneli geleneksel uygulama seansları (Faz 5). Düz liste
// (docs/design-principles.md §3): arama + tarih → durum çipleri → çerçevesiz
// tablo. Satıra tıklayınca sağdan drawer'da seansın salt-okunur görünümü
// (vücut haritası dahil); düzenleme hasta sekmesinde — "Hastaya git".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Spa as SpaIcon } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useListClinicPracticeSessionsMutation } from "../../store/traditionalApi";
import { DataTableCard } from "../../components/organisms";
import { FormDatePicker } from "../../components/atoms";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SORTERS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  patientNameOf,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import {
  PracticeSessionDetail,
  PendingConsentBanner,
  SESSION_STATUS,
  personName,
  formatDateTimeTr,
} from "../../components/organisms/PracticeSession";

const COLS = 5;

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "pending_consent", label: "Onay bekliyor" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal edildi" },
  { value: "warning", label: "Uyarılı" },
];

const SEGMENT_EMPTY = {
  pending_consent: "Onay bekleyen seans yok.",
  completed: "Tamamlanmış seans yok.",
  cancelled: "İptal edilmiş seans yok.",
  warning: "Uygunluk uyarısıyla kaydedilmiş seans yok.",
};

const toYMD = (value) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const segmentOf = (r) => r.status || "completed";
const extraSegmentsOf = (r) => ((r.cautionsAcknowledged || []).length ? ["warning"] : []);
const practitionerOf = (r) => personName(r.practitioner || r.createdBy);

const matches = (r, text) =>
  patientNameOf(r.patient).toLocaleLowerCase("tr").includes(text) ||
  String(r.patient?.tcNo || "").includes(text) ||
  String(r.practiceName || "").toLocaleLowerCase("tr").includes(text) ||
  practitionerOf(r).toLocaleLowerCase("tr").includes(text);

const SORT_FNS = { name: SORTERS.name((r) => r.patient), date: SORTERS.date((r) => r.performedAt) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

export default function PracticeSessionsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const canUpdate = useHasPermission("sessions:update");
  const canManageConsent = useHasPermission("consents:manage");

  const [listSessions, { isLoading }] = useListClinicPracticeSessionsMutation();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(() => rows.find((r) => r._id === selectedId) || null, [rows, selectedId]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listSessions({ token }).unwrap();
      setRows(res?.data?.list || []);
    } catch (_) {
      showError("Seanslar yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listSessions, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Onay bekleyen seansta yapılan işlem (yeniden gönder / kağıtla onayla / iptal) sonrası satırı
  // yerinde güncelle (hasta bilgisi liste kaydında kalır), ardından listeyi tazele
  const handlePendingChanged = useCallback(
    (updated) => {
      if (!updated?._id) return;
      setRows((prev) =>
        prev.map((r) => (r._id === updated._id ? { ...r, ...updated, patient: r.patient || updated.patient } : r))
      );
      refresh();
    },
    [refresh]
  );

  const dated = useMemo(
    () => (dateFilter ? rows.filter((r) => toYMD(r.performedAt) === dateFilter) : rows),
    [rows, dateFilter]
  );

  const list = useClientList(dated, {
    segmentOf,
    extraSegmentsOf,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  const busy = isLoading && !loaded;
  const empty = list.searchText
    ? { title: "Seans bulunamadı", message: `“${list.searchText}” aramanızla eşleşen seans yok. Aramayı değiştirin.` }
    : dateFilter
    ? { title: "Seans bulunamadı", message: "Seçilen günde kayıtlı seans yok." }
    : list.segment !== "all"
    ? { title: "Bu filtrede seans yok", message: SEGMENT_EMPTY[list.segment] }
    : { title: "Henüz seans yok", message: `Seanslar ${terms.patientLower} detayındaki Uygulamalar sekmesinden kaydedilir.` };

  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <>
      <ClinicRecordListLayout
        icon={<SpaIcon sx={{ fontSize: 22 }} />}
        title="Uygulamalar"
        subtitle="Geleneksel uygulama seansları"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={`${terms.patient} adı, TC no, uygulama veya uygulayıcı ile ara`}
        filters={
          <FormDatePicker
            placeholder="Seans tarihi"
            value={dateFilter}
            onChange={(v) => {
              setDateFilter(v);
              list.setSegment(list.segment);
            }}
            sx={{ width: 180 }}
          />
        }
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Seans durumu"
      >
        <DataTableCard
      loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<SpaIcon />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <SortHead field="name" label={terms.patient} {...sortProps} sx={{ ...EDGE_L, width: "28%" }} />
                <PlainHead sx={{ width: "26%" }}>Uygulama</PlainHead>
                <PlainHead sx={{ width: "18%" }}>Uygulayıcı</PlainHead>
                <SortHead field="date" label="Tarih" defaultDir="desc" {...sortProps} sx={{ width: "14%" }} />
                <PlainHead sx={{ ...EDGE_R, width: "14%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((row) => {
                  const st = SESSION_STATUS[row.status] || SESSION_STATUS.completed;
                  const regionCount = (row.regions || []).length;
                  const warnCount = (row.cautionsAcknowledged || []).length;
                  const sub = [
                    regionCount ? `${regionCount} bölge` : null,
                    row.durationMin != null ? `${row.durationMin} dk` : null,
                    warnCount ? `${warnCount} uyarı görüldü` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <ClickableRow key={row._id} onClick={() => setSelectedId(row._id)} palette={palette}>
                      <PatientCell patient={row.patient} />
                      <TextCell main={row.practiceName || null} sub={sub || null} />
                      <TextCell main={practitionerOf(row) || null} />
                      <DateCell value={row.performedAt} />
                      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                        <StatusPill label={st.label} tone={st.tone} palette={palette} />
                      </TableCell>
                    </ClickableRow>
                  );
                })
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <RecordEditorDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        icon={<SpaIcon />}
        title={selected ? patientNameOf(selected.patient) : "Seans"}
        subtitle={selected ? `${selected.practiceName || "Seans"} · ${formatDateTimeTr(selected.performedAt)}` : ""}
        patientLink={selected?.patient?._id ? `/Hastalar/${selected.patient._id}?tab=uygulamalar` : null}
      >
        {selected && (
          <div style={{ height: "100%", overflowY: "auto" }}>
            <PendingConsentBanner
              session={selected}
              token={token}
              patientName={patientNameOf(selected.patient)}
              canUpdate={canUpdate}
              canManageConsent={canManageConsent}
              onChanged={handlePendingChanged}
            />
            <PracticeSessionDetail session={selected} />
          </div>
        )}
      </RecordEditorDrawer>
    </>
  );
}
